import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight, Phone, Mail, BrainCircuit, ShieldCheck, Code2, CloudCog, Workflow, Heart, Landmark, Factory, ShoppingBag, Zap, Truck, Briefcase, Building2, BookOpen, FileText, FlaskConical, Award, Handshake, Layers, MapPin, Leaf, Network, Users, Target, Search, GraduationCap, Sparkles, Scale } from 'lucide-react';
import { StrategyCallModal } from '@/hubspot/FormComponents';
import SiteSearch from '@/components/SiteSearch';

const servicePillars = [
  {
  title: 'AI & Data',
  icon: BrainCircuit,
  desc: 'Machine learning, analytics, and agentic AI',
  links: [
  { label: 'Data Foundation', href: '/services/ai-data/data-foundation' },
  { label: 'Agentic AI', href: '/services/ai-data/agentic-ai' },
  { label: 'Generative AI', href: '/services/ai-data/generative-ai' },
  { label: 'Analytics & ML', href: '/services/ai-data/analytics-ml' },
  { label: 'Responsible AI', href: '/services/ai-data/responsible-ai' },
  ],
  href: '/services/ai-data',
  },
  {
  title: 'Cybersecurity',
  icon: ShieldCheck,
  desc: 'Risk management, compliance, and OneProtect',
  links: [
  { label: 'OneProtect (SOC)', href: '/services/cybersecurity/oneprotect' },
  { label: 'Security Assessments', href: '/services/cybersecurity/security-assessments' },
  { label: 'Compliance', href: '/services/cybersecurity/compliance' },
  { label: 'Threat Detection & Response', href: '/services/cybersecurity/incident-response' },
  { label: 'VAPT', href: '/services/cybersecurity/vapt' },
  { label: 'Security Architecture', href: '/services/cybersecurity/security-architecture' },
  { label: 'Security-First IT Ops', href: '/services/cybersecurity/security-first-it-ops' },
  { label: 'GRC', href: '/services/cybersecurity/grc' },
  ],
  href: '/cybersecurity',
  },
  {
  title: 'Application Development',
  icon: Code2,
  desc: 'Custom apps, MVP development, and mobile',
  links: [
  { label: 'Custom Development', href: '/services/application-development/custom-development' },
  { label: 'ERP Implementation', href: '/services/application-development/erp' },
  { label: 'CRM Solutions', href: '/services/application-development/crm' },
  { label: 'Mobile Development', href: '/services/application-development/mobile' },
  { label: 'API & Integration', href: '/services/application-development/api-integration' },
  { label: 'QA & Testing', href: '/services/application-development/qa-testing' },
  { label: 'MVP Development', href: '/services/application-development/mvp-development' },
  ],
  href: '/services/application-development',
  badge: 'Idea to MVP in 4 weeks',
  },
  {
  title: 'Cloud & Infrastructure',
  icon: CloudCog,
  desc: 'Strategy, migration, and cloud-native',
  links: [
  { label: 'Cloud Strategy', href: '/services/cloud/strategy' },
  { label: 'Cloud Migration', href: '/services/cloud/migration' },
  { label: 'Legacy to Cloud', href: '/services/cloud/legacy-modernization' },
  { label: 'Cloud-Native Development', href: '/services/cloud/cloud-native' },
  { label: 'Multi-Cloud & Hybrid', href: '/services/cloud/multi-cloud' },
  { label: 'FinOps & Cost Optimization', href: '/services/cloud/finops' },
  { label: 'DevOps & SRE', href: '/services/cloud/devops-sre' },
  ],
  href: '/services/cloud',
  },
  {
  title: 'Modernization & Automation',
  icon: Workflow,
  desc: 'Digital transformation and process automation',
  links: [
  { label: 'Digital Transformation Consulting', href: '/services/modernization-automation/consulting' },
  { label: 'Process Automation', href: '/services/modernization-automation/process-automation' },
  { label: 'IoT Solutions', href: '/services/modernization-automation/iot' },
  { label: 'Industry 4.0 & Smart Manufacturing', href: '/services/modernization-automation/industry-4-0' },
  { label: 'Emerging Technologies', href: '/services/modernization-automation/emerging-tech' },
  ],
  href: '/services/modernization-automation',
  },
  {
  title: 'Build Your MVP',
  icon: Sparkles,
  desc: 'From idea to product in 4 weeks',
  links: [
  { label: 'MVP Development', href: '/services/application-development/mvp-development' },
  ],
  href: '/services/application-development/mvp-development',
  badge: 'Idea to MVP in 4 weeks',
  },
];

const industriesItems = [
  { title: 'Healthcare', icon: Heart, desc: 'Patient outcomes, compliance, interoperability', href: '/industries/healthcare' },
  { title: 'Financial Services', icon: Landmark, desc: 'Risk modeling, fraud detection, regulatory', href: '/industries/financial-services' },
  { title: 'Manufacturing', icon: Factory, desc: 'Predictive maintenance, supply chain', href: '/industries/manufacturing' },
  { title: 'Retail & CPG', icon: ShoppingBag, desc: 'Omnichannel commerce, personalization', href: '/industries/retail' },
  { title: 'Energy & Utilities', icon: Zap, desc: 'Grid modernization, sustainability', href: '/industries/energy-utilities' },
  { title: 'Logistics & Transport', icon: Truck, desc: 'Fleet optimization, real-time visibility', href: '/industries/logistics-transport' },
  { title: 'Professional Services', icon: Briefcase, desc: 'Resource management, project delivery', href: '/industries/professional-services' },
  { title: 'Public Sector', icon: Building2, desc: 'Citizen services, secure infrastructure', href: '/industries/public-sector' },
];

const insightsItems = [
  { title: 'Blog', icon: BookOpen, desc: 'Latest thinking on technology and business', href: '/insights' },
  { title: 'Whitepapers', icon: FileText, desc: 'Deep dives and practical frameworks', href: '/insights' },
  { title: 'Case Studies', icon: FlaskConical, desc: 'Real results from real engagements', href: '/case-studies' },
];

const aboutItems = [
  { title: 'Company', icon: Building2, desc: 'Our story, mission, and values', href: '/about' },
  { title: 'Mission & Values', icon: Target, desc: 'What we believe shapes what we build', href: '/about/values' },
  { title: 'Diversity & Inclusion', icon: Scale, desc: 'Building stronger teams through diversity', href: '/about/diversity' },
  { title: 'Our Brands', icon: Network, desc: 'The Flynaut ecosystem of 7 brands', href: '/about/brands' },
  { title: 'Partners', icon: Handshake, desc: 'AWS, Microsoft, and technology alliances', href: '/partners' },
  { title: 'Careers', icon: Award, desc: 'Join the team building the future', href: '/careers' },
  { title: 'Contact', icon: MapPin, desc: 'Offices, inquiries, and how to reach us', href: '/contact' },
];

const UTILITY_HEIGHT = 36;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [hideNav, setHideNav] = useState(false);
  const [showStrategyCall, setShowStrategyCall] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
  const handler = () => {
  setScrolled(window.scrollY > 40);
  const section = document.querySelector('[data-testid="build-protect-section"]');
  if (section) {
  const rect = section.getBoundingClientRect();
  setHideNav(rect.top <= 0 && rect.bottom > window.innerHeight);
  }
  };
  window.addEventListener('scroll', handler, { passive: true });
  return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
  document.body.style.overflow = mobileOpen ? 'hidden' : '';
  return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleMouseEnter = (key) => {
  clearTimeout(timeoutRef.current);
  setActiveMenu(key);
  };
  const handleMouseLeave = () => {
  timeoutRef.current = setTimeout(() => setActiveMenu(null), 200);
  };

  // Compute nav state classes (no conflicting translate/opacity)
  let utilityNavClass, mainNavClass;

  if (hideNav) {
  // During immersive Build+Protect section - both hidden
  utilityNavClass = 'opacity-0 -translate-y-full pointer-events-none';
  mainNavClass = 'opacity-0 -translate-y-full pointer-events-none top-0';
  } else if (scrolled) {
  // Scrolled - utility slides away, main nav takes top with glass bg
  utilityNavClass = 'opacity-0 -translate-y-full pointer-events-none';
  mainNavClass = 'opacity-100 translate-y-0 bg-white/95 backdrop-blur-md shadow-brand-md top-0';
  } else {
  // At top - utility visible, main nav sits below it
  utilityNavClass = 'opacity-100 translate-y-0';
  mainNavClass = 'opacity-100 translate-y-0 bg-white top-0 xl:top-9';
  }

  return (
  <>
  {/* ===== Utility Nav ===== */}
  <div
  data-testid="utility-nav"
  className={`hidden xl:block bg-navy-900 text-navy-400 text-xs fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${utilityNavClass}`}
  style={{ height: `${UTILITY_HEIGHT}px`, lineHeight: `${UTILITY_HEIGHT}px` }}
  >
  <div className="max-w-content mx-auto px-8 flex items-center justify-end gap-6 h-full">
  <a href="tel:+1234567890" className="flex items-center gap-1.5 hover:text-white transition-colors duration-150">
  <Phone size={11} /> +1 (XXX) XXX-XXXX
  </a>
  <a href="mailto:info@flynaut.com" className="flex items-center gap-1.5 hover:text-white transition-colors duration-150">
  <Mail size={11} /> info@flynaut.com
  </a>
  <a href="/contact" className="hover:text-orange-500 transition-colors duration-150 font-medium">Contact</a>
  </div>
  </div>

  {/* ===== Main Nav ===== */}
  <nav
  data-testid="navbar"
  className={`fixed left-0 right-0 z-[99] transition-all duration-500 ${mainNavClass}`}
  >
  <div className="max-w-content mx-auto px-6 xl:px-8 flex items-center justify-between h-[88px]">
  {/* Logo - 2x bigger, contained within nav */}
  <a href="/" data-testid="navbar-logo" className="flex-shrink-0 mr-8">
  <img
  src="https://cdn.flynaut.com/images/Logo.png"
  alt="Flynaut"
  width="180"
  height="64"
  className="h-14 xl:h-16 w-auto"
  />
  </a>

  {/* Desktop Center Links - xl breakpoint (1280px+) */}
  <div className="hidden xl:flex items-center gap-1 flex-1 justify-center">
  {/* Services Mega Menu */}
  <div
  className="relative"
  onMouseEnter={() => handleMouseEnter('services')}
  onMouseLeave={handleMouseLeave}
  >
  <button
  data-testid="nav-link-services"
  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${
  activeMenu === 'services' ? 'text-orange-500' : 'text-navy-700 hover:text-navy-800'
  }`}
  >
  Services
  <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'services' ? 'rotate-180' : ''}`} />
  </button>

  <AnimatePresence>
  {activeMenu === 'services' && (
  <motion.div
  initial={{ opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, y: 8 }}
  transition={{ duration: 0.2 }}
  onMouseEnter={() => handleMouseEnter('services')}
  onMouseLeave={handleMouseLeave}
  className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-brand-xl border border-navy-200 overflow-hidden"
  style={{ width: '920px' }}
  data-testid="services-mega-menu"
  >
  {/* Header */}
  <div className="px-8 pt-6 pb-4 border-b border-navy-100 flex items-center justify-between">
  <div>
  <p className="text-navy-800 font-bold text-base">Our Services</p>
  <p className="text-navy-600 text-xs mt-0.5">Five practices. One integrated team. Zero gaps between them.</p>
  </div>
  <a href="/assessment" className="inline-flex items-center gap-1 text-xs font-medium border border-navy-200 rounded-full px-4 py-1.5 text-navy-600 hover:border-orange-300 hover:text-orange-500 transition-colors">
  Get a Technology Assessment
  </a>
  </div>

  {/* Service grid with sub-links */}
  <div className="px-8 py-5">
  <div className="grid grid-cols-3 gap-x-8 gap-y-5">
  {servicePillars.map((pillar) => {
  const Icon = pillar.icon;
  return (
  <div key={pillar.title}>
  <a href={pillar.href} className="group flex items-start gap-2.5 mb-2">
  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-orange-100 transition-colors">
  <Icon size={16} className="text-orange-500" strokeWidth={1.5} />
  </div>
  <div>
  <p className="text-navy-800 font-semibold text-sm group-hover:text-orange-500 transition-colors">
  {pillar.title}
  </p>
  </div>
  </a>
  <div className="flex flex-col gap-0.5 pl-[42px]">
  {pillar.links.map((link) => (
  <a key={link.label} href={link.href} className="text-navy-700 text-xs py-1 hover:text-orange-500 transition-colors">
  {link.label}
  </a>
  ))}
  </div>
  </div>
  );
  })}
  </div>
  </div>

  {/* Footer bar */}
  <div className="bg-navy-50 px-8 py-3 flex items-center justify-between border-t border-navy-100">
  <span className="text-navy-500 text-xs">Not sure where to start?</span>
  <a href="/assessment" className="inline-flex items-center gap-1.5 text-orange-500 text-xs font-semibold hover:text-orange-600 transition-colors">
  Get a Free Technology Assessment <ArrowRight size={11} />
  </a>
  </div>
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  {/* Industries Dropdown */}
  <div className="relative" onMouseEnter={() => handleMouseEnter('industries')} onMouseLeave={handleMouseLeave}>
  <button
  data-testid="nav-link-industries"
  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${activeMenu === 'industries' ? 'text-orange-500' : 'text-navy-700 hover:text-navy-800'}`}
  >
  Industries
  <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'industries' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {activeMenu === 'industries' && (
  <motion.div
  initial={{ opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, y: 8 }}
  transition={{ duration: 0.2 }}
  onMouseEnter={() => handleMouseEnter('industries')}
  onMouseLeave={handleMouseLeave}
  className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-brand-xl border border-navy-200 overflow-hidden"
  style={{ width: '560px' }}
  data-testid="industries-mega-menu"
  >
  <div className="px-6 pt-5 pb-2 border-b border-navy-100">
  <p className="text-navy-800 font-bold text-base">Industries</p>
  <p className="text-navy-600 text-xs mt-0.5">Deep expertise across sectors that matter.</p>
  </div>
  <div className="px-6 py-4 grid grid-cols-2 gap-x-6 gap-y-1">
  {industriesItems.map((item) => {
  const Icon = item.icon;
  return (
  <a key={item.title} href={item.href} className="group flex items-start gap-2.5 py-2">
  <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-orange-100 transition-colors">
  <Icon size={14} className="text-orange-500" strokeWidth={1.5} />
  </div>
  <div>
  <p className="text-navy-800 font-medium text-sm group-hover:text-orange-500 transition-colors">{item.title}</p>
  <p className="text-navy-600 text-[11px] leading-snug">{item.desc}</p>
  </div>
  </a>
  );
  })}
  </div>
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  {/* Insights Dropdown */}
  <div className="relative" onMouseEnter={() => handleMouseEnter('insights')} onMouseLeave={handleMouseLeave}>
  <button
  data-testid="nav-link-insights"
  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${activeMenu === 'insights' ? 'text-orange-500' : 'text-navy-700 hover:text-navy-800'}`}
  >
  Insights
  <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'insights' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {activeMenu === 'insights' && (
  <motion.div
  initial={{ opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, y: 8 }}
  transition={{ duration: 0.2 }}
  onMouseEnter={() => handleMouseEnter('insights')}
  onMouseLeave={handleMouseLeave}
  className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-brand-xl border border-navy-200 overflow-hidden"
  style={{ width: '320px' }}
  data-testid="insights-mega-menu"
  >
  <div className="px-6 pt-5 pb-2 border-b border-navy-100">
  <p className="text-navy-800 font-bold text-base">Insights</p>
  <p className="text-navy-600 text-xs mt-0.5">Ideas that drive decisions.</p>
  </div>
  <div className="px-6 py-4 flex flex-col gap-1">
  {insightsItems.map((item) => {
  const Icon = item.icon;
  return (
  <a key={item.title} href={item.href} className="group flex items-start gap-2.5 py-2">
  <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-orange-100 transition-colors">
  <Icon size={14} className="text-orange-500" strokeWidth={1.5} />
  </div>
  <div>
  <p className="text-navy-800 font-medium text-sm group-hover:text-orange-500 transition-colors">{item.title}</p>
  <p className="text-navy-600 text-[11px] leading-snug">{item.desc}</p>
  </div>
  </a>
  );
  })}
  </div>
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  {/* About Dropdown */}
  <div className="relative" onMouseEnter={() => handleMouseEnter('about')} onMouseLeave={handleMouseLeave}>
  <button
  data-testid="nav-link-about"
  className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ${activeMenu === 'about' ? 'text-orange-500' : 'text-navy-700 hover:text-navy-800'}`}
  >
  About
  <ChevronDown size={14} className={`transition-transform duration-200 ${activeMenu === 'about' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {activeMenu === 'about' && (
  <motion.div
  initial={{ opacity: 0, y: 8 }}
  animate={{ opacity: 1, y: 0 }}
  exit={{ opacity: 0, y: 8 }}
  transition={{ duration: 0.2 }}
  onMouseEnter={() => handleMouseEnter('about')}
  onMouseLeave={handleMouseLeave}
  className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-brand-xl border border-navy-200 overflow-hidden"
  style={{ width: '520px' }}
  data-testid="about-mega-menu"
  >
  <div className="px-8 pt-6 pb-3 border-b border-navy-100">
  <p className="text-navy-800 font-bold text-base">About Flynaut</p>
  <p className="text-navy-600 text-xs mt-0.5">20+ years transforming technology.</p>
  </div>
  <div className="px-8 py-5 grid grid-cols-2 gap-x-8 gap-y-4">
  {aboutItems.map((item) => {
  const Icon = item.icon;
  return (
  <a key={item.title} href={item.href} className="group flex items-start gap-2.5 py-2">
  <div className="w-7 h-7 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-orange-100 transition-colors">
  <Icon size={14} className="text-orange-500" strokeWidth={1.5} />
  </div>
  <div>
  <p className="text-navy-800 font-medium text-sm group-hover:text-orange-500 transition-colors">{item.title}</p>
  <p className="text-navy-600 text-[11px] leading-snug">{item.desc}</p>
  </div>
  </a>
  );
  })}
  </div>
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  <a href="/case-studies" data-testid="nav-link-case-studies" className="px-3 py-2 text-sm font-medium text-navy-700 hover:text-navy-800 rounded-lg transition-colors">
  Case Studies
  </a>
  <a href="/careers" data-testid="nav-link-careers" className="px-3 py-2 text-sm font-medium text-navy-700 hover:text-navy-800 rounded-lg transition-colors">
  Careers
  </a>
  </div>

  {/* Desktop Right Actions - xl only */}
  <div className="hidden xl:flex items-center gap-3 flex-shrink-0 ml-8">
  <a
  href="/contact"
  data-testid="nav-contact-btn"
  className="text-orange-500 border border-orange-500 hover:bg-orange-50 rounded-full px-5 py-2.5 text-sm font-medium transition-all whitespace-nowrap"
  >
  Contact
  </a>
  <button
  onClick={() => setShowSearch(true)}
  data-testid="nav-search-btn"
  className="text-navy-400 hover:text-orange-500 transition-colors p-2"
  aria-label="Search"
  >
  <Search size={18} />
  </button>
  <button
  onClick={() => setShowStrategyCall(true)}
  data-testid="nav-book-call-btn"
  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap"
  >
  Book a Call
  </button>
  </div>

  {/* Mobile/Tablet Toggle - below xl */}
  <button
  data-testid="mobile-menu-toggle"
  onClick={() => setMobileOpen(!mobileOpen)}
  className="xl:hidden text-navy-800 p-2"
  aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
  >
  {mobileOpen ? <X size={24} /> : <Menu size={24} />}
  </button>
  </div>
  </nav>

  {/* ===== Mobile / Tablet Drawer ===== */}
  <AnimatePresence>
  {mobileOpen && (
  <>
  <motion.div
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  exit={{ opacity: 0 }}
  className="fixed inset-0 bg-black/30 z-[110] xl:hidden"
  onClick={() => setMobileOpen(false)}
  />
  <motion.div
  data-testid="mobile-menu"
  initial={{ x: '100%' }}
  animate={{ x: 0 }}
  exit={{ x: '100%' }}
  transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
  className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white z-[120] xl:hidden overflow-y-auto"
  >
  {/* Close button */}
  <div className="flex items-center justify-between p-6 border-b border-navy-100">
  <img
  src="https://cdn.flynaut.com/images/Logo.png"
  alt="Flynaut"
  width="120"
  height="40"
  className="h-10"
  />
  <button onClick={() => setMobileOpen(false)} className="text-navy-600 p-1">
  <X size={24} />
  </button>
  </div>

  <div className="p-6 pb-32">
  {/* Services Accordion */}
  <div className="border-b border-navy-100">
  <button
  onClick={() => setMobileExpanded(mobileExpanded === 'services' ? null : 'services')}
  className="w-full flex items-center justify-between py-4 text-navy-800 font-medium text-base"
  >
  Services
  <ChevronDown size={18} className={`text-navy-400 transition-transform duration-200 ${mobileExpanded === 'services' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {mobileExpanded === 'services' && (
  <motion.div
  initial={{ height: 0, opacity: 0 }}
  animate={{ height: 'auto', opacity: 1 }}
  exit={{ height: 0, opacity: 0 }}
  className="overflow-hidden pb-4"
  >
  {servicePillars.map((pillar) => (
  <a key={pillar.title} href={pillar.href} onClick={() => setMobileOpen(false)} className="block py-2.5 pl-4 text-sm text-navy-600 hover:text-orange-500">
  {pillar.title}
  </a>
  ))}
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  {/* Industries Accordion */}
  <div className="border-b border-navy-100">
  <button
  onClick={() => setMobileExpanded(mobileExpanded === 'industries' ? null : 'industries')}
  className="w-full flex items-center justify-between py-4 text-navy-800 font-medium text-base"
  >
  Industries
  <ChevronDown size={18} className={`text-navy-400 transition-transform duration-200 ${mobileExpanded === 'industries' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {mobileExpanded === 'industries' && (
  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pb-4">
  {industriesItems.map((item) => (
  <a key={item.title} href={item.href} onClick={() => setMobileOpen(false)} className="block py-2.5 pl-4 text-sm text-navy-600 hover:text-orange-500">{item.title}</a>
  ))}
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  {/* Insights Accordion */}
  <div className="border-b border-navy-100">
  <button
  onClick={() => setMobileExpanded(mobileExpanded === 'insights' ? null : 'insights')}
  className="w-full flex items-center justify-between py-4 text-navy-800 font-medium text-base"
  >
  Insights
  <ChevronDown size={18} className={`text-navy-400 transition-transform duration-200 ${mobileExpanded === 'insights' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {mobileExpanded === 'insights' && (
  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pb-4">
  {insightsItems.map((item) => (
  <a key={item.title} href={item.href} onClick={() => setMobileOpen(false)} className="block py-2.5 pl-4 text-sm text-navy-600 hover:text-orange-500">{item.title}</a>
  ))}
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  {/* About Accordion */}
  <div className="border-b border-navy-100">
  <button
  onClick={() => setMobileExpanded(mobileExpanded === 'about' ? null : 'about')}
  className="w-full flex items-center justify-between py-4 text-navy-800 font-medium text-base"
  >
  About
  <ChevronDown size={18} className={`text-navy-400 transition-transform duration-200 ${mobileExpanded === 'about' ? 'rotate-180' : ''}`} />
  </button>
  <AnimatePresence>
  {mobileExpanded === 'about' && (
  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden pb-4">
  {aboutItems.map((item) => (
  <a key={item.title} href={item.href} onClick={() => setMobileOpen(false)} className="block py-2.5 pl-4 text-sm text-navy-600 hover:text-orange-500">{item.title}</a>
  ))}
  </motion.div>
  )}
  </AnimatePresence>
  </div>

  <a href="/case-studies" onClick={() => setMobileOpen(false)} className="block py-4 text-navy-800 font-medium text-base border-b border-navy-100">Case Studies</a>
  <a href="/careers" onClick={() => setMobileOpen(false)} className="block py-4 text-navy-800 font-medium text-base border-b border-navy-100">Careers</a>

  {/* Contact info in drawer */}
  <div className="mt-6 pt-6 border-t border-navy-100 space-y-3 text-sm text-navy-500">
  <a href="tel:+1234567890" className="flex items-center gap-2 hover:text-orange-500"><Phone size={14} /> +1 (XXX) XXX-XXXX</a>
  <a href="mailto:info@flynaut.com" className="flex items-center gap-2 hover:text-orange-500"><Mail size={14} /> info@flynaut.com</a>
  </div>
  </div>

  {/* Sticky bottom CTAs */}
  <div className="fixed bottom-0 right-0 w-[85%] max-w-sm bg-white border-t border-navy-200 p-4 flex gap-3 z-[130]">
  <a href="/contact" className="flex-1 text-center border border-orange-500 text-orange-500 rounded-full py-3 text-sm font-medium">
  Contact
  </a>
  <button onClick={() => { setMobileOpen(false); setShowStrategyCall(true); }} className="flex-1 text-center bg-orange-500 text-white rounded-full py-3 text-sm font-medium">
  Book a Call
  </button>
  </div>
  </motion.div>
  </>
  )}
  </AnimatePresence>
  <StrategyCallModal isOpen={showStrategyCall} onClose={() => setShowStrategyCall(false)} />
  <SiteSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
  </>
  );
}
